import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Grid, TextField, MenuItem, Button, Card, CardContent } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '../../api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#ffc658'];

const CorrectionDashboard = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const defaultStart = `${yyyy}-${mm}-01`;
  const defaultEnd = `${yyyy}-${mm}-${dd}`;

  const [filters, setFilters] = useState({
    emp_id: '',
    start_date: defaultStart,
    end_date: defaultEnd,
    reason: '',
    department_id: '',
    designation_id: ''
  });
  const [dropdowns, setDropdowns] = useState({ departments: [], designations: [], reasons: [] });
  const [stats, setStats] = useState({
    cards: {},
    top_employees: [],
    top_departments: [],
    top_designations: [],
    date_wise: [],
    reason_wise: []
  });

  useEffect(() => {
    api.get('/correction-dashboard/filters-data').then(res => setDropdowns(res.data)).catch(console.error);
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    const query = new URLSearchParams(Object.entries(filters).filter(([_, v]) => v !== '')).toString();
    try {
      const res = await api.get(`/correction-dashboard/stats?${query}`);
      setStats(res.data);
    } catch (err) { console.error(err); }
  };

  const getDisplayDateText = () => {
    if (filters.start_date && filters.end_date) {
      const start = new Date(filters.start_date);
      const end = new Date(filters.end_date);
      if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return start.toLocaleString('default', { month: 'long', year: 'numeric' });
      }
      return `${filters.start_date} to ${filters.end_date}`;
    }
    return 'All Time';
  };

  const truncateText = (text, maxLength = 13) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: 2 }}>   {/* full width with side margins */}

      {/* ROW 1: FILTERS */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderRadius: 2 }}>
        <TextField size="small" label="Emp ID" value={filters.emp_id} onChange={e => setFilters({...filters, emp_id: e.target.value})} sx={{ minWidth: 110, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
        <TextField size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={filters.start_date} onChange={e => setFilters({...filters, start_date: e.target.value})} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
        <TextField size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }} value={filters.end_date} onChange={e => setFilters({...filters, end_date: e.target.value})} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
        <TextField select size="small" label="Reason" sx={{ minWidth: 120, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} value={filters.reason} onChange={e => setFilters({...filters, reason: e.target.value})}>
          <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All</MenuItem>
          {dropdowns.reasons.map(r => <MenuItem key={r} value={r} sx={{ fontSize: '0.85rem' }}>{r}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Department" sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} value={filters.department_id} onChange={e => setFilters({...filters, department_id: e.target.value})}>
          <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All</MenuItem>
          {dropdowns.departments.map(d => <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.85rem' }}>{d.department_name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Designation" sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} value={filters.designation_id} onChange={e => setFilters({...filters, designation_id: e.target.value})}>
          <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All</MenuItem>
          {dropdowns.designations.map(d => <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.85rem' }}>{d.designation_name}</MenuItem>)}
        </TextField>
        <Button variant="contained" size="small" onClick={fetchStats} disableElevation sx={{ py: 1 }}>Apply Filters</Button>
      </Paper>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2, pl: 0.5, fontWeight: 500 }}>
        Showing data for: <strong style={{ color: '#1976d2' }}>{getDisplayDateText()}</strong>
      </Typography>

      {/* ROW 2: COUNT CARDS � using Grid (unchanged) */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'Total Submitted', value: stats.cards.total || 0, color: '#1976d2' },
          { label: 'Approved', value: stats.cards.approved || 0, color: '#2e7d32' },
          { label: 'Rejected', value: stats.cards.rejected || 0, color: '#d32f2f' },
          { label: 'Pending', value: stats.cards.pending || 0, color: '#ed6c02' }
        ].map((card, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Card sx={{ borderTop: `4px solid ${card.color}`, boxShadow: 1, borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: card.color }}>{card.value}</Typography>
                <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{card.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ROW 3: THREE CHARTS � 33% each using flexbox */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        {[
          { title: 'Top 10 Employees', data: stats.top_employees, color: '#8884d8', yKey: 'emp_id' },
          { title: 'Top 10 Departments', data: stats.top_departments, color: '#82ca9d', yKey: 'name' },
          { title: 'Top 10 Designations', data: stats.top_designations, color: '#ffc658', yKey: 'name' }
        ].map((chart, idx) => (
          <Box key={idx} sx={{ flex: '1 1 0', minWidth: { xs: '100%', md: 0 } }}>  {/* equal flex, stack on mobile */}
            <Paper sx={{ p: 1.5, height: 260, width: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2, boxShadow: 1 }}>
              <Typography variant="body2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#444' }}>{chart.title}</Typography>
              <Box sx={{ flexGrow: 1, minHeight: 0, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chart.data} layout="vertical" margin={{ left: 0, right: 15, top: 5, bottom: 0 }}>
                    <XAxis type="number" allowDecimals={false} fontSize={10} tick={{ fill: '#777' }} />
                    <YAxis
                      dataKey={chart.yKey}
                      type="category"
                      width={chart.yKey === 'emp_id' ? 55 : 75}
                      fontSize={10}
                      tick={{ fill: '#333' }}
                      tickFormatter={(tick) => truncateText(tick, 12)}
                    />
                    <Tooltip cursor={{ fill: '#f5f5f5' }} contentStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="count" fill={chart.color} radius={[0, 3, 3, 0]} barSize={12} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Box>
        ))}
      </Box>

      {/* ROW 4: LINE (66%) + PIE (33%) � using flexbox with flex:2 and flex:1 */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '2 1 0', minWidth: { xs: '100%', md: 0 } }}>
          <Paper sx={{ p: 1.5, height: 260, width: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2, boxShadow: 1 }}>
            <Typography variant="body2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#444' }}>Date-wise Submissions</Typography>
            <Box sx={{ flexGrow: 1, minHeight: 0, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.date_wise} margin={{ left: -10, right: 15, top: 5, bottom: 0 }}>
                  <XAxis dataKey="date_str" fontSize={10} tick={{ fill: '#777' }} />
                  <YAxis allowDecimals={false} fontSize={10} tick={{ fill: '#777' }} />
                  <Tooltip contentStyle={{ fontSize: '12px' }} />
                  <Line type="monotone" dataKey="count" stroke="#ff7300" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: { xs: '100%', md: 0 } }}>
          <Paper sx={{ p: 1.5, height: 260, width: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2, boxShadow: 1 }}>
            <Typography variant="body2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#444' }}>Submissions by Reason</Typography>
            <Box sx={{ flexGrow: 1, minHeight: 0, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stats.reason_wise} dataKey="count" nameKey="reason_category" cx="50%" cy="45%" outerRadius={70} label={{ fontSize: 10 }}>
                    {stats.reason_wise.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: '12px' }} />
                  <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default CorrectionDashboard;